import React, { useState } from 'react';
import { Mutation } from 'react-apollo';
import { navigate } from '@reach/router';
import gql from 'graphql-tag';
import Select from 'react-select';
import Button from '@material-ui/core/Button';
import TextArea from '../common/textarea';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DropzoneArea } from 'material-ui-dropzone';
import classes from './add-product.module.scss';
import Input from '../input/input';
import Error from '../common/error';
import allCategories from './categories';
import { GET_PRODUCTS, GET_MY_PRODUCTS } from '../../queries';
import { BACKEND_URL } from '../../back-end-url';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { MAX_IMAGE_FILE_SIZE } from '../../constants';
import { Checkbox } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const ADD_PRODUCT = gql`
  mutation addProduct(
    $name: String!
    $description: String!
    $price: Float!
    $imagesUrls: [String!]!
    $categories: [CategoryInput!]!
    $active: Boolean!
  ) {
    addProduct(
      name: $name
      description: $description
      price: $price
      imagesUrls: $imagesUrls
      categories: $categories
      active: $active
    )
  }
`;

export const uploadFile = (file: File) => {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.onreadystatechange = () => {
      if (req.readyState !== 4) {
        return;
      }

      if (req.status !== 200) {
        reject({ message: `Cannot upload file. ${req.statusText}` });
      } else {
        resolve(JSON.parse(req.response));
      }
    };

    const formData = new FormData();
    formData.append('file', file, file.name);
    req.open('POST', `${BACKEND_URL}/upload`, true);
    req.setRequestHeader('authorization', localStorage.getItem('token') || '');
    req.send(formData);
  });
};

export const uploadFiles = (files: File[]): Promise<any> => {
  return Promise.all(files.map(uploadFile));
};

const AddProduct = () => {
  const [error, setError] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);

  const addProduct = (formValues, addProductMutation) => () => {
    const { name, description, price, files, categories, active } = formValues;
    setShowSpinner(true);

    uploadFiles(files)
      .then((uploadInfo: Array<{ secure_url: string; [key: string]: any }>) => {
        const imagesUrls = uploadInfo.map(
          (info: { secure_url: string }) => info.secure_url
        );
        return imagesUrls;
      })
      .then(imagesUrls => {
        setShowSpinner(false);
        return addProductMutation({
          variables: {
            name,
            description,
            price: +price,
            categories,
            imagesUrls,
            active
          }
        });
      })
      .then(() => {
        navigate('/manage-products');
      })
      .catch(e => {
        scrollToTop();
        setShowSpinner(false);
        if (e.graphQLErrors && e.graphQLErrors.length) {
          setError(e.graphQLErrors[0].message);
        }
      });
  };

  return (
    <div className={classes.container}>
      {error && <Error errorMessage={error} />}
      <Formik
        initialValues={{
          name: '',
          description: '',
          price: '',
          files: [],
          categories: [],
          active: true
        }}
        onSubmit={() => true}
        validationSchema={Yup.object().shape({
          name: Yup.string().required('Вкажіть назву товару'),
          description: Yup.string().required('Додайте опис товару'),
          price: Yup.number().required('Вкажіть ціну'),
          files: Yup.array()
            .required('Виберіть фото')
            .test(
              'is-file-bigger-than-1mb',
              'Завеликий розмір файлу(ів)',
              files => {
                const filesThatExceedFileSizeLimit = files.filter(
                  file => file.size > MAX_IMAGE_FILE_SIZE
                );
                return filesThatExceedFileSizeLimit.length === 0;
              }
            ),
          categories: Yup.array().required('Вкажіть категорії')
        })}
      >
        {props => {
          const {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            isValid
          } = props;

          return (
            <form>
              <Input
                label="Назва"
                name="name"
                error={touched.name && errors.name}
                value={values.name}
                changed={handleChange}
                unfocused={handleBlur}
              />
              <TextArea
                label="Опис"
                name="description"
                error={touched.description && errors.description}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Input
                type="number"
                value={values.price}
                name="price"
                error={touched.price && errors.price}
                label="Ціна"
                changed={handleChange}
                unfocused={handleBlur}
              />
              <span className={classes['choose-image-title']}>
                Виберіть фото розміром до 1 Мб
              </span>
              <DropzoneArea
                acceptedFiles={['image/*']}
                filesLimit={10}
                showAlerts={false}
                onChange={(selectedFiles: any) => {
                  setFieldValue('files', selectedFiles);
                  setFieldTouched('files', true);
                }}
              />
              {touched.files && errors.files && (
                <span style={{ color: 'red' }}>{errors.files}</span>
              )}
              <Select
                className={classes['category-select']}
                options={allCategories}
                value={values.categories}
                onChange={(selectedCategories: any) => {
                  setFieldValue('categories', selectedCategories);
                  setFieldTouched('categories', true);
                }}
                placeholder="Виберіть категорію(ї)"
                isMulti
              />
              {touched.categories && errors.categories && (
                <span style={{ color: 'red' }}>{errors.categories}</span>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.active}
                    onChange={() => {
                      setFieldValue('active', !values.active);
                    }}
                  />
                }
                label="Показувати продукт"
              />

              <div style={{ marginTop: '50px' }}>
                <Mutation
                  mutation={ADD_PRODUCT}
                  refetchQueries={[
                    { query: GET_PRODUCTS },
                    { query: GET_MY_PRODUCTS }
                  ]}
                >
                  {addProductMutation => {
                    return (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={showSpinner || !isValid}
                        style={{ textTransform: 'capitalize' }}
                        data-e2e-id={'add-button'}
                        onClick={addProduct(
                          {
                            name: values.name,
                            description: values.description,
                            price: values.price,
                            files: values.files,
                            categories: values.categories,
                            active: values.active
                          },
                          addProductMutation
                        )}
                      >
                        Додати
                        {showSpinner && (
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: '10px' }}
                          />
                        )}
                      </Button>
                    );
                  }}
                </Mutation>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddProduct;
